import React, { lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { LanguageProvider } from "./context/LanguageContext";
import { Spinner } from "./components/ui";

// Landing + auth are the first screens visitors see, so they stay eager.
import Landing from "./pages/Landing";
import { Login, Register, AdminLogin } from "./pages/Auth";

// Everything behind sign-in is loaded on demand.
const Storefront = lazy(() => import("./pages/Storefront"));
const Feed = lazy(() => import("./pages/Feed"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const BuyerOrders = lazy(() => import("./pages/BuyerOrders"));
const Wallet = lazy(() => import("./pages/Wallet"));
const MerchantOnboarding = lazy(() => import("./pages/MerchantOnboarding"));
const SellerDashboard = lazy(() => import("./pages/SellerDashboard"));
const SellerProducts = lazy(() => import("./pages/SellerProducts"));
const SellerOrders = lazy(() => import("./pages/SellerOrders"));
const SellerPayouts = lazy(() => import("./pages/SellerPayouts"));
const SellerInventory = lazy(() => import("./pages/SellerInventory"));
const SellerPurchaseOrders = lazy(() => import("./pages/SellerPurchaseOrders"));
const SellerWarehouse = lazy(() => import("./pages/SellerWarehouse"));
const SellerFinance = lazy(() => import("./pages/SellerFinance"));
const SellerBulkTools = lazy(() => import("./pages/SellerBulkTools"));
const SellerKyc = lazy(() => import("./pages/SellerKyc"));
const AdminKyc = lazy(() => import("./pages/AdminKyc"));
const Support = lazy(() => import("./pages/Support"));
const AdminSupport = lazy(() => import("./pages/AdminSupport"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));

const AdminSellers = lazy(() => import("./pages/AdminSellers"));
const AdminOrders = lazy(() => import("./pages/AdminOrders"));
const AdminPayouts = lazy(() => import("./pages/AdminPayouts"));
const AdminDeposits = lazy(() => import("./pages/AdminDeposits"));
const AdminWithdrawals = lazy(() => import("./pages/AdminWithdrawals"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminReturns = lazy(() => import("./pages/AdminReturns"));
const AdminReviews = lazy(() => import("./pages/AdminReviews"));
const AdminCoupons = lazy(() => import("./pages/AdminCoupons"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminPurchaseOrders = lazy(() => import("./pages/AdminPurchaseOrders"));
const AdminWarehouse = lazy(() => import("./pages/AdminWarehouse"));
const AdminMerchants = lazy(() => import("./pages/AdminMerchants"));
const AdminStores = lazy(() => import("./pages/AdminStores"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));

function Loader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-base">
      <Spinner className="h-8 w-8" />
    </div>
  );
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/shop" replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : user.role === "seller" ? "/seller" : "/shop"} replace />;
  return children;
}

// First visit in a browser session: send people to the homepage instead of
// landing straight on the sign-in page via a direct link.
function HomeFirst({ children }) {
  let entered = true;
  try {
    entered = sessionStorage.getItem("sc_entered") === "1";
    if (!entered) sessionStorage.setItem("sc_entered", "1");
  } catch {}
  if (!entered) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/shop" element={<Protected><Storefront /></Protected>} />
      <Route path="/feed" element={<Protected><Feed /></Protected>} />
      <Route path="/product/:id" element={<Protected><ProductDetail /></Protected>} />
      <Route path="/cart" element={<Protected><Cart /></Protected>} />
      <Route path="/login" element={<HomeFirst><GuestOnly><Login /></GuestOnly></HomeFirst>} />
      <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
      <Route path="/control-panel-login" element={<GuestOnly><AdminLogin /></GuestOnly>} />
      <Route path="/merchant-onboarding" element={<Protected><MerchantOnboarding /></Protected>} />

      <Route path="/orders" element={<Protected><BuyerOrders /></Protected>} />
      <Route path="/wallet" element={<Protected><Wallet /></Protected>} />
      <Route path="/become-seller" element={<Protected><MerchantOnboarding /></Protected>} />

      <Route path="/seller" element={<Protected roles={["seller", "admin"]}><SellerDashboard /></Protected>} />
      <Route path="/seller/products" element={<Protected roles={["seller", "admin"]}><SellerProducts /></Protected>} />
      <Route path="/seller/warehouse" element={<Protected roles={["seller", "admin"]}><SellerWarehouse /></Protected>} />
      <Route path="/seller/purchase-orders" element={<Protected roles={["seller", "admin"]}><SellerPurchaseOrders /></Protected>} />
      <Route path="/seller/orders" element={<Protected roles={["seller", "admin"]}><SellerOrders /></Protected>} />
      <Route path="/seller/finance" element={<Protected roles={["seller", "admin"]}><SellerFinance /></Protected>} />
      <Route path="/seller/bulk-tools" element={<Protected roles={["seller", "admin"]}><SellerBulkTools /></Protected>} />
      <Route path="/seller/payouts" element={<Protected roles={["seller", "admin"]}><SellerPayouts /></Protected>} />
      <Route path="/seller/inventory" element={<Protected roles={["seller", "admin"]}><SellerInventory /></Protected>} />
      <Route path="/seller/kyc" element={<Protected roles={["seller", "admin"]}><SellerKyc /></Protected>} />


      <Route path="/support" element={<Protected><Support /></Protected>} />
      <Route path="/admin/support" element={<Protected roles={["admin"]}><AdminSupport /></Protected>} />

      <Route path="/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
      <Route path="/admin/sellers" element={<Protected roles={["admin"]}><AdminSellers /></Protected>} />
      <Route path="/admin/kyc" element={<Protected roles={["admin"]}><AdminKyc /></Protected>} />

      <Route path="/admin/warehouse" element={<Protected roles={["admin"]}><AdminWarehouse /></Protected>} />
      <Route path="/admin/purchase-orders" element={<Protected roles={["admin"]}><AdminPurchaseOrders /></Protected>} />
      <Route path="/admin/orders" element={<Protected roles={["admin"]}><AdminOrders /></Protected>} />
      <Route path="/admin/returns" element={<Protected roles={["admin"]}><AdminReturns /></Protected>} />
      <Route path="/admin/reviews" element={<Protected roles={["admin"]}><AdminReviews /></Protected>} />
      <Route path="/admin/coupons" element={<Protected roles={["admin"]}><AdminCoupons /></Protected>} />
      <Route path="/admin/analytics" element={<Protected roles={["admin"]}><AdminAnalytics /></Protected>} />
      <Route path="/admin/deposits" element={<Protected roles={["admin"]}><AdminDeposits /></Protected>} />
      <Route path="/admin/withdrawals" element={<Protected roles={["admin"]}><AdminWithdrawals /></Protected>} />
      <Route path="/admin/payouts" element={<Protected roles={["admin"]}><AdminPayouts /></Protected>} />
      <Route path="/admin/stores" element={<Protected roles={["admin"]}><AdminStores /></Protected>} />
      <Route path="/admin/merchants" element={<Protected roles={["admin"]}><AdminMerchants /></Protected>} />
      <Route path="/admin/settings" element={<Protected roles={["admin"]}><AdminSettings /></Protected>} />
      <Route path="/admin/users" element={<Protected roles={["admin"]}><AdminUsers /></Protected>} />

      <Route path="*" element={<Navigate to="/shop" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <CartProvider>
            <BrowserRouter>
              <Suspense fallback={<Loader />}>
                <AppRoutes />
              </Suspense>
            </BrowserRouter>
            <Toaster position="top-center" theme="dark" richColors />
          </CartProvider>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}
